import { FilterOptionInformation } from "@studyportals/search-filters";
import { FilterKey } from "@studyportals/search-filters";
import { IFilterOptions } from "./IFilterOptions";
import { AttendanceOptions } from "./AttendanceOptions";
import { DisciplineOptions } from "./DisciplineOptions";
import { injectable } from "inversify";
import { CountryOptions } from "./CountryOptions";
import { ContinentOptions } from "./ContinentOptions";
import { UniversityOptions } from "./UniversityOptions";
import { AreaOptions } from "./AreaOptions";
import { PortalType } from "@studyportals/domain-client";
import { AttendanceOptionsForDistanceLearning } from "./AttendanceOptionsForDistanceLearning";


@injectable()
export class FilterOptions {
	private readonly countryOptions: CountryOptions;
	private readonly filterOptions: readonly IFilterOptions[];

	public constructor(window: Window) {
		const portalType = window.PortalType;

		this.countryOptions = new CountryOptions();
		this.filterOptions = [
			this.countryOptions,
			new AreaOptions(),
			new ContinentOptions(),
			new DisciplineOptions(),
			new UniversityOptions(),
			portalType === PortalType.DISTANCE_LEARNING
				? new AttendanceOptionsForDistanceLearning()
				: new AttendanceOptions(),
		];
	}

	public identifyRequiredFilters(): FilterKey[] {
		return this.filterOptions.filter((o) => o.isRequired()).map((o) => o.getFilterKey());
	}

	public identifyFilters(): FilterKey[] {
		return this.filterOptions.map((o) => o.getFilterKey());
	}

	public getFor(key: FilterKey): FilterOptionInformation[] {
		return this.getFilterOptionsFor(key)?.determineOptions() || [];
	}

	public getForByValue(key: FilterKey, value?: string | undefined): FilterOptionInformation | null {
		return this.getFor(key).find((o) => o.optionValue === value) || null;
	}

	public isValidFor(key: FilterKey, value: string): boolean {
		return this.getFilterOptionsFor(key)?.isValid(value) ?? false;
	}

	public isInvalidFor(key: FilterKey, value: string): boolean {
		return !this.isValidFor(key, value);
	}

	public isRequiredFor(key: FilterKey): boolean {
		return this.getFilterOptionsFor(key)?.isRequired() ?? false;
	}

	public getValuesOfCountryOptionsForIsoCodes(isoCodes: readonly string[]): string[] {
		return this.countryOptions.getValuesForIsoCodes(isoCodes);
	}

	public getTopCountryOptions(): string[] {
		return this.countryOptions.getTopCountries(window.PortalType);
	}

	private getFilterOptionsFor(key: FilterKey): IFilterOptions | null {
		return this.filterOptions.find((o) => o.supports(key)) ?? null;
	}
}
